import { useCallback, useEffect, useSyncExternalStore } from 'react';

export function chatDraftKey(owner, conversationId) {
  const cid = Number(conversationId) > 0 ? String(Number(conversationId)) : 'default';
  return `aiDiary.chat.draft.v2${owner ? '.' + owner : ''}.${cid}`;
}

let database;
function openDraftDatabase() {
  if (!database) database = new Promise((resolve, reject) => {
    const request = indexedDB.open('noesa-chat-drafts', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('attachments');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Draft storage blocked'));
  }).catch(error => { database = null; throw error; });
  return database;
}

async function attachmentTransaction(key, value, write = false) {
  const db = await openDraftDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('attachments', write ? 'readwrite' : 'readonly');
    const objectStore = tx.objectStore('attachments');
    const hasValue = Array.isArray(value) ? value.length : value?.attachments?.length || value?.restored?.length;
    const request = write ? (hasValue ? objectStore.put(value, key) : objectStore.delete(key)) : objectStore.get(key);
    tx.oncomplete = () => resolve(write ? undefined : (request.result || []));
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Draft storage aborted'));
  });
}

const newRevision = () => typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
const fileHashes = new WeakMap();
export async function fingerprintDraftFiles(files = []) {
  return Promise.all(files.map(async file => {
    if (!fileHashes.has(file)) {
      const hash = (async () => {
        const bytes = typeof file.arrayBuffer === 'function' ? await file.arrayBuffer() : await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error || new Error('Cannot read attachment'));
          reader.onabort = () => reject(new Error('Attachment read aborted'));
          reader.readAsArrayBuffer(file);
        });
        if (typeof crypto === 'undefined' || !crypto.subtle) throw new Error('Secure file hashing is unavailable');
        const digest = await crypto.subtle.digest('SHA-256', bytes);
        return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
      })();
      fileHashes.set(file, hash);
      hash.catch(() => fileHashes.delete(file));
    }
    return JSON.stringify([file.name, file.size, file.type, await fileHashes.get(file)]);
  }));
}

// Store File objects in IndexedDB, not JSON or expiring blob URLs. Writes stay in edit order.
export function createChatComposerDraftStore({ readText = key => localStorage.getItem(key) || '',
  writeText = (key, value) => localStorage.setItem(key, value),
  readFiles = key => attachmentTransaction(key), writeFiles = (key, value) => attachmentTransaction(key, value, true) } = {}) {
  const entries = new Map();
  const listeners = new Set();
  const notify = () => listeners.forEach(fn => fn());
  const get = key => {
    if (!entries.has(key)) {
      let text = '', storageError = false;
      try { text = readText(key); } catch { storageError = true; }
      let state = {};
      try { state = JSON.parse(readText(`${key}.state.v1`) || '{}'); } catch {}
      // The journal atomically records restored text and its receipt ID; the old key remains a plain-text mirror.
      const journalCurrent = typeof state.text === 'string' && (text === state.text || text === state.previousText);
      entries.set(key, { snapshot: { key, text: journalCurrent ? state.text : text, attachments: [], attachmentIds: [],
        textRevision: journalCurrent ? state.textRevision : newRevision(), revision: 0, ready: false, storageError },
        savedText: text, textRestored: state.restored || [], fileRestored: [], textError: storageError, fileError: false,
        writes: Promise.resolve(true) });
    }
    return entries.get(key);
  };
  const publish = (entry, patch) => { entry.snapshot = { ...entry.snapshot, ...patch }; notify(); };
  const persistText = entry => {
    try {
      const { key, text, textRevision } = entry.snapshot;
      writeText(`${key}.state.v1`, JSON.stringify({ text, textRevision, previousText: entry.savedText, restored: entry.textRestored }));
      writeText(key, text);
      entry.savedText = text; entry.textError = false;
    } catch { entry.textError = true; }
    publish(entry, { storageError: entry.textError || entry.fileError });
    return !entry.textError;
  };
  const persistFiles = entry => {
    const { key, attachments, attachmentIds } = entry.snapshot;
    const value = { attachments, attachmentIds, restored: entry.fileRestored };
    entry.writes = entry.writes.catch(() => {}).then(() => writeFiles(key, value)).then(() => {
      entry.fileError = false;
      publish(entry, { storageError: entry.textError }); return true;
    }, () => {
      entry.fileError = true;
      publish(entry, { storageError: true }); return false;
    });
  };
  const store = {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    getSnapshot(key) { return get(key).snapshot; },
    load(key) {
      const entry = get(key);
      if (!entry.loading) entry.loading = Promise.resolve().then(() => readFiles(key)).then(value => {
        const attachments = Array.isArray(value) ? value : value.attachments || [];
        const attachmentIds = value.attachmentIds?.length === attachments.length ? value.attachmentIds : attachments.map(newRevision);
        entry.fileRestored = value.restored || [];
        entry.filesLoaded = true; entry.fileError = false;
        publish(entry, { attachments, attachmentIds, ready: true });
        if (attachments.length && !value.attachmentIds) persistFiles(entry);
      }, () => { entry.loading = null; entry.fileError = true; publish(entry, { ready: true, storageError: true }); });
      return entry.loading;
    },
    setText(key, value) {
      const entry = get(key);
      const text = String(typeof value === 'function' ? value(entry.snapshot.text) : value);
      if (text === entry.snapshot.text) return;
      entry.snapshot = { ...entry.snapshot, text, textRevision: newRevision(), revision: entry.snapshot.revision + 1 };
      persistText(entry);
    },
    setAttachments(key, value) {
      const entry = get(key);
      if (!entry.snapshot.ready) return store.load(key).then(() => store.setAttachments(key, value));
      const attachments = typeof value === 'function' ? value(entry.snapshot.attachments) : value;
      const used = new Set();
      const attachmentIds = attachments.map(file => {
        const index = entry.snapshot.attachments.findIndex((previous, i) => previous === file && !used.has(i));
        if (index < 0) return newRevision();
        used.add(index); return entry.snapshot.attachmentIds[index];
      });
      publish(entry, { attachments, attachmentIds, revision: entry.snapshot.revision + 1 });
      persistFiles(entry);
    },
    consume(submitted) {
      const entry = get(submitted.key);
      if (submitted.text !== undefined && (entry.snapshot.text !== submitted.text || entry.snapshot.attachments.length)) return false;
      if (entry.snapshot.revision !== submitted.revision && !(submitted.text !== undefined && entry.snapshot.revision === 0)) return false;
      store.setText(submitted.key, '');
      store.setAttachments(submitted.key, []);
      return true;
    },
    async captureSubmission(draft) {
      const entry = get(draft.key);
      if (!persistText(entry)) throw new Error('Cannot persist draft identity');
      const fingerprints = await fingerprintDraftFiles(draft.attachments);
      if (fingerprints.length !== (draft.attachmentIds || []).length) throw new Error('Draft attachment identity is missing');
      if (fingerprints.length && !await store.whenSaved(draft.key)) throw new Error('Cannot persist attachments');
      return { key: draft.key, revision: draft.revision, text: draft.text, textRevision: draft.textRevision,
        files: fingerprints.map((fingerprint, index) => ({ id: draft.attachmentIds[index], fingerprint })) };
    },
    async consumeConfirmed(submitted, canApply = () => true) {
      const entry = get(submitted.key);
      await store.load(submitted.key);
      if (!canApply() || !entry.filesLoaded) return false;
      if (!submitted.files) { store.consume(submitted); return true; }
      let current, fingerprints;
      do {
        current = entry.snapshot;
        fingerprints = await fingerprintDraftFiles(current.attachments);
        if (!canApply()) return false;
      } while (current !== entry.snapshot);
      const kept = current.attachments.filter((file, index) => !submitted.files.some(sent =>
        sent.id === current.attachmentIds[index] && sent.fingerprint === fingerprints[index]));
      if (current.text === submitted.text && current.textRevision === submitted.textRevision) store.setText(submitted.key, '');
      if (kept.length !== current.attachments.length) store.setAttachments(submitted.key, kept);
      await store.whenSaved(submitted.key);
      return canApply() && !entry.textError && !entry.fileError;
    },
    async stageRecovery(key, id, files) {
      await writeFiles(`${key}.recovery.${encodeURIComponent(id)}`, files);
    },
    async readRecovery(key, id) {
      const value = await readFiles(`${key}.recovery.${encodeURIComponent(id)}`);
      return Array.isArray(value) ? value : [];
    },
    async clearRecovery(key, id) {
      try { await writeFiles(`${key}.recovery.${encodeURIComponent(id)}`, []); } catch {}
    },
    async isRestored(key, id, hasFiles = false) {
      const entry = get(key);
      await store.load(key);
      return entry.textRestored.includes(id) && !entry.textError
        && (!hasFiles || (entry.fileRestored.includes(id) && !entry.fileError));
    },
    async restore(key, recovery, canApply = () => true) {
      const entry = get(key);
      await store.load(key);
      if (!entry.filesLoaded) return false;
      const recoveredFiles = recovery.attachments || [];
      const recoveredHashes = await fingerprintDraftFiles(recoveredFiles);
      let current, currentHashes;
      do {
        current = entry.snapshot;
        currentHashes = await fingerprintDraftFiles(current.attachments);
        if (!canApply()) return false;
      } while (current !== entry.snapshot);
      if (!entry.textRestored.includes(recovery.id)) {
        const text = !recovery.text || current.text === recovery.text ? current.text
          : current.text ? `${current.text}\n\n${recovery.text}` : recovery.text;
        entry.textRestored = [...entry.textRestored, recovery.id];
        entry.snapshot = { ...entry.snapshot, text, textRevision: newRevision(), revision: entry.snapshot.revision + 1 };
      }
      const textSaved = persistText(entry);
      if (recoveredFiles.length && !entry.fileRestored.includes(recovery.id)) {
        const remaining = [...currentHashes];
        const additions = recoveredFiles.filter((file, index) => {
          const existing = remaining.indexOf(recoveredHashes[index]);
          if (existing < 0) return true;
          remaining.splice(existing, 1); return false;
        });
        entry.fileRestored = [...entry.fileRestored, recovery.id];
        store.setAttachments(key, [...entry.snapshot.attachments, ...additions]);
      } else if (recoveredFiles.length && entry.fileError) persistFiles(entry);
      await store.whenSaved(key);
      return canApply() && textSaved && !entry.fileError;
    },
    whenSaved(key) { return get(key).writes; },
  };
  return store;
}

export const chatComposerDraftStore = createChatComposerDraftStore();

export function useChatComposerDraft(owner, conversationId) {
  const key = chatDraftKey(owner, conversationId);
  const snapshot = useCallback(() => chatComposerDraftStore.getSnapshot(key), [key]);
  const draft = useSyncExternalStore(chatComposerDraftStore.subscribe, snapshot, snapshot);
  useEffect(() => { chatComposerDraftStore.load(key); }, [key]);
  const setInputText = useCallback(value => chatComposerDraftStore.setText(key, value), [key]);
  const setAttachments = useCallback(value => chatComposerDraftStore.setAttachments(key, value), [key]);
  return { ...draft, inputText: draft.text, setInputText, setAttachments };
}
